import type React from 'react';
import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import { BaseModel } from '@cotera/client/v0/actions/framework/model.base';
import type { MessageClass } from '@cotera/sdk/core';
import type {
  HijackerQueueItem,
  HijackerQueueState,
} from './input-hijackers/types';
import {
  getApprovalHijacker,
  getInputHijackers,
} from './input-hijackers/registry';
import { uniqBy } from 'lodash';

/**
 * Model that manages the hijacker queue state.
 * Used by PresentationalChat and sub-agent renderers to coordinate
 * tool approval and input hijacking.
 *
 * This model is created at the top level (CocoChat) and shared
 * via ModelScopeFactory. Sub-agents can push their tool parts
 * directly to this model instead of using recursive context.
 *
 * The queue is derived from:
 * 1. Messages from the main chat (scanned for incomplete tool parts)
 * 2. Additional tool parts from nested sub-agents
 */
export class HijackerQueueModel extends BaseModel {
  private readonly processedIds: Set<string> = new Set();
  // Internal state for activeIndex and canComplete (user-controlled navigation)
  private readonly navigationStateWatchable: Watchable<{
    activeIndex: number;
    canComplete: boolean;
  }>;
  // Optional callback to get messages watchable (for lazy resolution)
  private messagesWatchable: ReadonlyWatchable<MessageClass[]>;

  private readonly subAgentMessagesWatchable: Watchable<MessageClass[]> =
    Watchable.fromValue<MessageClass[]>([], {
      equalityFn: (a, b) =>
        a.length === b.length && a.every((m) => b.some((m2) => m2.id === m.id)),
    });

  // Conversation ID for targeting the correct conversation
  private readonly conversationId: string;

  // Stable progress tracking for the step indicator. The queue physically
  // shrinks as items are resolved (an approved tool loses its `approvalStatus`
  // and drops out of the scan), so we cannot derive "Step N of M" from the live
  // queue — it would count down. Instead we remember every item id seen since
  // the queue was last empty; `total` is that count and stays fixed for the
  // batch while resolved items fall out of the queue.
  private readonly sessionStatsWatchable: Watchable<{
    total: number;
    seen: ReadonlySet<string>;
  }>;

  // Custom buttons registered by the active hijacker
  private readonly customSkipButtonWatchable: Watchable<React.ReactNode | null>;
  private readonly customSubmitButtonWatchable: Watchable<React.ReactNode | null>;

  readonly queueStateWatchable: ReadonlyWatchable<HijackerQueueState>;
  readonly hijackedToolCallIds: ReadonlyWatchable<Set<string>>;
  /** Watchable for custom skip button registered by active hijacker */
  readonly customSkipButton: ReadonlyWatchable<React.ReactNode | null>;
  /** Watchable for custom submit button registered by active hijacker */
  readonly customSubmitButton: ReadonlyWatchable<React.ReactNode | null>;

  constructor(
    conversationId: string,
    messagesWatchable: ReadonlyWatchable<MessageClass[]>
  ) {
    super(`hijacker-queue-${conversationId}`);
    this.conversationId = conversationId;
    this.navigationStateWatchable = Watchable.fromValue<{
      activeIndex: number;
      canComplete: boolean;
    }>({
      activeIndex: -1,
      canComplete: true,
    });
    this.messagesWatchable = messagesWatchable;

    // Initialize custom button watchables
    this.customSkipButtonWatchable =
      Watchable.fromValue<React.ReactNode | null>(null);
    this.customSubmitButtonWatchable =
      Watchable.fromValue<React.ReactNode | null>(null);

    this.sessionStatsWatchable = Watchable.fromValue<{
      total: number;
      seen: ReadonlySet<string>;
    }>({ total: 0, seen: new Set() });

    // Create the internal state watchable once and cache it
    this._internalStateWatchable = this.createInternalStateWatchable();

    // Keep the stable batch total in sync as the queue grows/shrinks. Updating
    // this from a subscription (rather than inside the derive) keeps the derive
    // side-effect free. The subscription only fires on change, so seed it with
    // the current snapshot for models created after messages have loaded.
    this.updateSessionStats(this._internalStateWatchable.snapshot().queue);
    this._internalStateWatchable.subscribe((s) =>
      this.updateSessionStats(s.queue)
    );

    // Bind callbacks once for stable references
    this._setCanComplete = this.setCanComplete.bind(this);
    this._onComplete = this.onComplete.bind(this);
    this._onSkip = this.onSkip.bind(this);
    this._onPrevious = this.onPrevious.bind(this);
    this._onDismissAll = this.onDismissAll.bind(this);

    this.queueStateWatchable = Watchable.from((get) => {
      const s = get(this._internalStateWatchable);
      const session = get(this.sessionStatsWatchable);
      const remaining = s.queue.length;
      // `total` may briefly lag behind a freshly grown queue (the subscription
      // that updates it fires after this derive), so clamp up to `remaining`.
      const stepTotal = Math.max(session.total, remaining);
      const stepCompleted = Math.max(0, stepTotal - remaining);
      return {
        queue: s.queue,
        activeIndex: s.activeIndex,
        activeItem:
          s.activeIndex < 0 || s.activeIndex >= s.queue.length
            ? null
            : s.queue[s.activeIndex] ?? null,
        totalPending: s.queue.filter(
          (i) => i.status === 'pending' || i.status === 'active'
        ).length,
        completedCount: s.queue.filter((i) => i.status === 'completed').length,
        canComplete: s.canComplete,
        setCanComplete: this._setCanComplete,
        onComplete: this._onComplete,
        onSkip: this._onSkip,
        onPrevious: this._onPrevious,
        onDismissAll: this._onDismissAll,
        hasMultiple: stepTotal > 1,
        stepIndicator: {
          current: stepTotal === 0 ? 0 : Math.min(stepTotal, stepCompleted + 1),
          total: stepTotal,
          completed: stepCompleted,
        },
      };
    });

    this.hijackedToolCallIds = Watchable.from((get) => {
      const toolCallIds = get(this.queueStateWatchable).queue.map(
        (item) => item.part.toolCallId
      );

      return new Set(toolCallIds);
    });

    // Expose readonly views of custom buttons
    this.customSkipButton = this.customSkipButtonWatchable;
    this.customSubmitButton = this.customSubmitButtonWatchable;
  }

  // Stable callback references (bound once in constructor)
  private readonly _setCanComplete: (canComplete: boolean) => void;
  private readonly _onComplete: () => void;
  private readonly _onSkip: () => void;
  private readonly _onPrevious: () => void;
  private readonly _onDismissAll: () => void;

  /**
   * Maintain the stable batch total used by the step indicator. Resolved items
   * fall out of the queue, so we accumulate every id seen while the queue is
   * non-empty and reset once it drains. Idempotent: re-running with the same
   * queue leaves the stats unchanged.
   */
  private updateSessionStats(queue: HijackerQueueItem[]): void {
    const current = this.sessionStatsWatchable.snapshot();

    if (queue.length === 0) {
      if (current.total !== 0) {
        this.sessionStatsWatchable.set({ total: 0, seen: new Set() });
      }
      return;
    }

    const seen = new Set(current.seen);
    for (const item of queue) {
      seen.add(item.id);
    }
    if (seen.size !== current.seen.size) {
      this.sessionStatsWatchable.set({ total: seen.size, seen });
    }
  }

  addSubAgentMessages(messages: MessageClass[]): void {
    this.subAgentMessagesWatchable.set(
      uniqBy(
        [...this.subAgentMessagesWatchable.snapshot(), ...messages],
        (m) => m.id
      )
    );
  }

  removeSubAgentMessages(messages: MessageClass[]): void {
    this.subAgentMessagesWatchable.set(
      this.subAgentMessagesWatchable
        .snapshot()
        .filter((m) => !messages.some((m2) => m2.id === m.id))
    );
  }

  // Computed watchable that builds the queue from messages + additional parts
  private computeQueue(
    messages: MessageClass[],
    navState: { activeIndex: number; canComplete: boolean }
  ): {
    queue: HijackerQueueItem[];
    activeIndex: number;
    canComplete: boolean;
  } {
    const hijackers = getInputHijackers();
    const approvalHijacker = getApprovalHijacker();
    const newItems: HijackerQueueItem[] = [];

    // Scan messages for incomplete tool parts
    for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
      const msg = messages[msgIdx];
      if (msg === undefined || !msg.isAssistantMessage()) {
        continue;
      }

      // Read `parts` once — it's a cached getter, but re-reading it in both the
      // loop condition and body needlessly re-invokes it per part.
      const parts = msg.parts ?? [];
      for (let partIdx = 0; partIdx < parts.length; partIdx++) {
        const p = parts[partIdx];
        if (!p?.isToolPart?.() || !p?.isIncomplete?.()) {
          continue;
        }

        const part = p;
        const toolKey = part.toolKey?.()?.toString?.() ?? '';
        const id = `${msg.id}-${partIdx}`;

        // Skip already processed items
        if (this.processedIds.has(id)) {
          continue;
        }

        // Check if this is an approval hijacker (highest priority)
        if (
          part.approvalStatus?.() === 'required' &&
          approvalHijacker !== null
        ) {
          newItems.push({
            id,
            toolKey,
            part,
            component: approvalHijacker.component,
            priority: Infinity,
            status: 'pending' as const,
            messageIndex: msgIdx,
            conversationId: msg.conversationId,
          });
          continue;
        }

        // Check for registered hijackers
        const hijacker = hijackers.find((h) => h.toolKey === toolKey);
        if (hijacker !== undefined) {
          if (hijacker.predicate?.(part)) {
            newItems.push({
              id,
              toolKey,
              part,
              component: hijacker.component,
              priority: hijacker.priority ?? 0,
              status: 'pending' as const,
              messageIndex: msgIdx,
              inputPlaceholder: hijacker.inputPlaceholder,
              conversationId: msg.conversationId,
            });
          }
        }
      }
    }

    // Sort by priority (desc), then by message order (asc)
    newItems.sort((a, b) => {
      if (a.priority !== b.priority) {
        return b.priority - a.priority;
      }
      return a.messageIndex - b.messageIndex;
    });

    // Set initial active index if not set
    let activeIndex = navState.activeIndex;
    if (activeIndex === -1 && newItems.length > 0) {
      activeIndex = 0;
    } else if (activeIndex >= newItems.length) {
      activeIndex = newItems.length > 0 ? newItems.length - 1 : -1;
    }

    // Update statuses based on active index
    const queue = newItems.map((item: HijackerQueueItem, index: number) => {
      if (index < activeIndex) {
        if (item.status === 'skipped') {
          return { ...item, status: 'skipped' as const };
        }
        return { ...item, status: 'completed' as const };
      }
      if (index === activeIndex) {
        return { ...item, status: 'active' as const };
      }
      return { ...item, status: 'pending' as const };
    });

    return {
      queue,
      activeIndex,
      canComplete: navState.canComplete,
    };
  }

  // Cached internal state watchable derived from messages + additional parts + navigation state
  private readonly _internalStateWatchable: ReadonlyWatchable<{
    queue: HijackerQueueItem[];
    activeIndex: number;
    canComplete: boolean;
  }>;

  private createInternalStateWatchable(): ReadonlyWatchable<{
    queue: HijackerQueueItem[];
    activeIndex: number;
    canComplete: boolean;
  }> {
    return Watchable.from((get) => {
      const navState = get(this.navigationStateWatchable);

      // Get messages from callback if available
      const messages = get(this.messagesWatchable);
      const subAgentMessages = get(this.subAgentMessagesWatchable);
      return this.computeQueue([...messages, ...subAgentMessages], navState);
    });
  }

  /**
   * Set whether the current step can be completed.
   */
  setCanComplete(canComplete: boolean): void {
    const state = this.navigationStateWatchable.snapshot();
    this.navigationStateWatchable.set({ ...state, canComplete });
  }

  /**
   * Mark current item as complete and move to next.
   */
  onComplete(): void {
    const state = this.navigationStateWatchable.snapshot();
    const queue = this._internalStateWatchable.snapshot().queue;

    if (!state.canComplete) {
      return;
    }

    const currentItem = queue[state.activeIndex];
    if (currentItem) {
      this.processedIds.add(currentItem.id);
    }

    // Clear custom buttons before navigating
    this.clearCustomButtons();

    const nextIndex = state.activeIndex + 1;
    if (nextIndex >= queue.length) {
      // All done - reset
      this.navigationStateWatchable.set({
        activeIndex: -1,
        canComplete: true,
      });
      return;
    }

    this.navigationStateWatchable.set({
      activeIndex: nextIndex,
      canComplete: true,
    });
  }

  /**
   * Skip current item and move to next.
   */
  onSkip(): void {
    const state = this.navigationStateWatchable.snapshot();
    const queue = this._internalStateWatchable.snapshot().queue;
    const currentItem = queue[state.activeIndex];

    if (currentItem) {
      this.processedIds.add(currentItem.id);
    }

    // Clear custom buttons before navigating
    this.clearCustomButtons();

    const nextIndex = state.activeIndex + 1;
    if (nextIndex >= queue.length) {
      // All done - reset
      this.navigationStateWatchable.set({
        activeIndex: -1,
        canComplete: true,
      });
      return;
    }

    this.navigationStateWatchable.set({
      activeIndex: nextIndex,
      canComplete: true,
    });
  }

  /**
   * Go back to previous item.
   */
  onPrevious(): void {
    const state = this.navigationStateWatchable.snapshot();
    if (state.activeIndex <= 0) {
      return;
    }
    // Clear custom buttons before navigating
    this.clearCustomButtons();
    this.navigationStateWatchable.set({
      activeIndex: state.activeIndex - 1,
      canComplete: true,
    });
  }

  /**
   * Mark all remaining items as skipped.
   */
  onDismissAll(): void {
    const queue = this._internalStateWatchable.snapshot().queue;
    for (const item of queue) {
      if (item.status === 'pending' || item.status === 'active') {
        this.processedIds.add(item.id);
      }
    }
    // Clear custom buttons when dismissing all
    this.clearCustomButtons();
    this.navigationStateWatchable.set({
      activeIndex: -1,
      canComplete: true,
    });
  }

  /**
   * Get the conversation ID for a tool call ID.
   * Returns undefined if the tool call is not in the queue.
   */
  getConversationIdForToolCall(toolCallId: string): string | undefined {
    const queue = this._internalStateWatchable.snapshot().queue;
    const item = queue.find((i) => i.part.toolCallId === toolCallId);
    return item?.conversationId;
  }

  /**
   * Check if an item has been processed (completed or skipped).
   */
  isProcessed(id: string): boolean {
    return this.processedIds.has(id);
  }

  /**
   * Clear processed IDs (useful when resetting).
   */
  clearProcessed(): void {
    this.processedIds.clear();
  }

  /**
   * Reset the entire queue (navigation state + processed IDs).
   * Additional tool parts are preserved unless clearParts is true.
   */
  reset(): void {
    this.navigationStateWatchable.set({
      activeIndex: -1,
      canComplete: true,
    });
    this.clearProcessed();
    this.clearCustomButtons();
  }

  /**
   * Register a custom skip button for the current hijacker step.
   * Call with null to unregister.
   */
  registerSkipButton(button: React.ReactNode | null): void {
    // Only update if actually different to prevent loops
    if (this.customSkipButtonWatchable.snapshot() === button) {
      return;
    }
    this.customSkipButtonWatchable.set(button);
  }

  /**
   * Register a custom submit/continue button for the current hijacker step.
   * Call with null to unregister.
   */
  registerSubmitButton(button: React.ReactNode | null): void {
    // Only update if actually different to prevent loops
    if (this.customSubmitButtonWatchable.snapshot() === button) {
      return;
    }
    this.customSubmitButtonWatchable.set(button);
  }

  /**
   * Clear custom buttons (called when navigating between steps).
   */
  private clearCustomButtons(): void {
    this.customSkipButtonWatchable.set(null);
    this.customSubmitButtonWatchable.set(null);
  }
}
